import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { PageHeader, LoadingTable, ErrorState, EmptyState } from '../components/States';
import { DataTable } from '../components/DataTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../lib/api';

export default function Piani() {
  const { active, reload: reloadActive } = useScenario();
  const { isAdmin } = useAuth();
  const [list, setList] = useState(null);
  const [error, setError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', year: '2026', duplicate_from: '' });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setError(false); api.getScenarios().then(setList).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);

  const activate = async (row) => {
    setBusy(true);
    try { await api.activateScenario(row.id); toast.success(`Piano "${row.name}" attivato`); load(); reloadActive(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.deleteScenario(confirm.id); toast.success('Piano eliminato'); setConfirm(null); load(); reloadActive(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const create = async () => {
    if (!form.name.trim()) { toast.error('Inserisci un nome'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, type: 'plan', year: parseInt(form.year) || 2026 };
      if (form.duplicate_from) payload.duplicate_from = form.duplicate_from;
      await api.createScenario(payload);
      toast.success('Piano creato'); setCreateOpen(false); setForm({ name: '', description: '', year: '2026', duplicate_from: '' }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (error) return (<div><PageHeader title="Piani" /><ErrorState onRetry={load} /></div>);
  if (!list) return (<div><PageHeader title="Piani" /><LoadingTable /></div>);

  const columns = [
    { key: 'name', header: 'Nome piano', render: (r) => <div className="flex items-center gap-2"><span className="font-medium">{r.name}</span>{r.is_active && <Badge className="bg-primary text-primary-foreground"><CheckCircle2 className="h-3 w-3 mr-1" />Attivo</Badge>}</div> },
    { key: 'year', header: 'Anno', align: 'right', render: (r) => r.year },
    { key: 'type', header: 'Tipo', render: (r) => <Badge variant="secondary">{r.type === 'control' ? 'Controllo' : 'Piano'}</Badge> },
    { key: 'description', header: 'Descrizione', render: (r) => <span className="text-muted-foreground text-xs">{r.description || '—'}</span> },
    { key: 'actions', header: '', sortable: false, align: 'right', render: (r) => (
      <div className="flex items-center justify-end gap-2">
        {!r.is_active && <Button size="sm" variant="outline" disabled={busy} onClick={() => activate(r)} data-testid="activate-plan-button">Attiva</Button>}
        {isAdmin && !r.is_active && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setConfirm(r)} data-testid="delete-plan-button"><Trash2 className="h-4 w-4" /></Button>}
      </div>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Piani" description={`Piano attivo: ${active?.name || '—'}`}
        actions={isAdmin && <Button onClick={() => setCreateOpen(true)} data-testid="new-plan-button"><Plus className="h-4 w-4 mr-1.5" />Nuovo piano</Button>} />
      {list.length === 0 ? <EmptyState title="Nessun piano" description="Crea il primo piano industriale." /> : <DataTable columns={columns} rows={list} />}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="form-modal">
          <DialogHeader><DialogTitle>Nuovo piano industriale</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="field-name" /></div>
            <div className="grid gap-1.5"><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid gap-1.5"><Label>Anno</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
            <div className="grid gap-1.5"><Label>Duplica dati da (opzionale)</Label>
              <Select value={form.duplicate_from} onValueChange={(v) => setForm({ ...form, duplicate_from: v })}>
                <SelectTrigger><SelectValue placeholder="Parti da zero" /></SelectTrigger>
                <SelectContent>{list.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button><Button onClick={create} disabled={saving} data-testid="form-modal-submit">{saving ? 'Creazione…' : 'Crea piano'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} title="Eliminare il piano?" description={confirm ? `"${confirm.name}" verrà eliminato definitivamente.` : ''} onConfirm={doDelete} loading={busy} />
    </div>
  );
}
